import { useCallback, useEffect, useState } from "react";
import { Eye, Inbox, LogOut, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/layout/Seo";
import { Logo } from "@/components/brand/Logo";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EnquiryDialog, StatusSelect, fmtDate } from "@/components/admin/EnquiryParts";
import { useAuth } from "@/context/AuthContext";
import { LEAD_STATUSES, SERVICES } from "@/content/navigation";
import { api, formatApiError } from "@/lib/api";

const Filter = ({ value, onChange, options, all, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11 w-full bg-white sm:w-[200px]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Stats = ({ stats }) => (
  <div className="grid grid-cols-3 gap-3 sm:grid-cols-6" data-testid="admin-stats">
    {[["Total", stats?.total ?? 0], ...LEAD_STATUSES.map((s) => [s, stats?.by_status?.[s] ?? 0])].map(([k, v]) => (
      <div key={k} className="rounded-xl border border-ink/10 bg-white p-4" data-testid={`admin-stat-${k.toLowerCase()}`}>
        <p className="text-[11px] uppercase tracking-[0.14em] text-ink/60">{k}</p>
        <p className="mt-1 font-display text-3xl font-light text-ink">{v}</p>
      </div>
    ))}
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [service, setService] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (service !== "all") params.service = service;
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const [list, s] = await Promise.all([api.get("/admin/enquiries", { params }), api.get("/admin/enquiries/stats")]);
      setItems(Array.isArray(list.data) ? list.data : []);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [service, status, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const changeStatus = async (id, next) => {
    try {
      const { data } = await api.patch(`/admin/enquiries/${id}`, { status: next });
      setItems((list) => list.map((e) => (e.id === id ? data : e)));
      setActive((a) => (a?.id === id ? data : a));
      const s = await api.get("/admin/enquiries/stats");
      setStats(s.data);
      toast.success(`Status set to ${next}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-ivory text-ink" data-testid="admin-dashboard">
      <Seo title="Enquiries | VION Tech Admin" description="VION Tech admin enquiries." noindex />
      <header className="bg-ink">
        <div className="shell flex items-center justify-between py-4">
          <Logo testid="admin-logo" />
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-white/70 sm:inline">{user?.email}</span>
            <button onClick={logout} className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/20 px-4 text-xs font-semibold uppercase tracking-[0.14em] text-white hover:border-gold" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="shell py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-plum-2">Admin</p>
            <h1 className="mt-2 font-display text-4xl font-light">Enquiries</h1>
          </div>
          <button onClick={load} className="inline-flex min-h-[44px] items-center gap-2 text-sm text-plum" data-testid="admin-refresh-btn"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
        </div>
        <div className="mt-8"><Stats stats={stats} /></div>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/50" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or organisation" className="h-11 bg-white pl-9 text-[16px]" data-testid="admin-search-input" />
          </div>
          <Filter value={service} onChange={setService} options={SERVICES} all="All services" testid="admin-filter-service" />
          <Filter value={status} onChange={setStatus} options={LEAD_STATUSES} all="All statuses" testid="admin-filter-status" />
        </div>
        <div className="mt-6 overflow-x-auto rounded-xl border border-ink/10 bg-white">
          <table className="w-full min-w-[860px] text-left text-sm" data-testid="enquiries-table">
            <thead className="border-b border-ink/10 text-[11px] uppercase tracking-[0.14em] text-ink/60">
              <tr><th className="p-4">Date</th><th className="p-4">Contact</th><th className="p-4">Service / Category</th><th className="p-4">Organisation</th><th className="p-4">Status</th><th className="p-4" /></tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-ink/5 align-top last:border-0 hover:bg-ivory/60" data-testid={`enquiry-row-${e.id}`}>
                  <td className="whitespace-nowrap p-4 text-ink/70">{fmtDate(e.created_at)}</td>
                  <td className="p-4"><p className="font-semibold">{e.name}</p><p className="text-ink/60">{e.email}</p>{e.phone && <p className="text-ink/60">{e.phone}</p>}</td>
                  <td className="p-4"><p>{e.service}</p><p className="text-xs text-plum-2">{e.category}</p></td>
                  <td className="p-4 text-ink/70">{e.organisation || "—"}</td>
                  <td className="p-4"><StatusSelect value={e.status} onChange={(s) => changeStatus(e.id, s)} testid={`enquiry-status-${e.id}`} /></td>
                  <td className="p-4 text-right"><button onClick={() => setActive(e)} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-ink/15 px-4 text-xs font-semibold hover:border-plum hover:text-plum" data-testid={`enquiry-view-${e.id}`}><Eye className="h-4 w-4" /> View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && items.length === 0 && (
            <div className="flex flex-col items-center py-16 text-ink/60" data-testid="enquiries-empty"><Inbox className="h-8 w-8" /><p className="mt-3">No enquiries found.</p></div>
          )}
        </div>
      </main>
      <EnquiryDialog enquiry={active} onClose={() => setActive(null)} onStatus={changeStatus} />
    </div>
  );
}
